import { useState } from "react";
import { Link, Redirect } from "wouter";
import { useAuth } from "../hooks/useAuth";
import {
  useGetCustomerMe,
  useCustomerInboxUnreadCount,
} from "@orbit/api-client-react";
import { Wordmark } from "../components/Brand";
import { BottomNav } from "../components/BottomNav";
import { DeletionScheduledBanner } from "../components/DeletionScheduledBanner";
import { VerifyChannelDialog } from "../components/VerifyChannelDialog";

/**
 * /profile — top-level account hub. Big-name identity block at the
 * top, then a list of sub-page navigation rows ordered by spec:
 *   identity → relationships → audit/data → delete.
 *
 * Phone/email verification: an inline "Verify" button appears next to
 * an unverified channel and opens a small dialog that requests an OTP
 * via the existing /auth/verify-{phone,email}/{request,confirm}
 * endpoints. The dedicated change-flows
 * (ProfilePhoneChange / ProfileEmailChange) still own the flow when
 * the *value* is changing — this dialog is for "I gave you the right
 * value at signup, now prove I own it" cases that the change-wizard
 * can't handle.
 */
export function ProfilePage() {
  const { isAuthenticated, signOut } = useAuth();
  const meQ = useGetCustomerMe({ enabled: isAuthenticated });
  const unreadQ = useCustomerInboxUnreadCount({
    enabled: isAuthenticated,
    refetchInterval: 60_000,
  });
  const unread = unreadQ.data?.unreadCount ?? 0;
  const [verifyOpen, setVerifyOpen] = useState<
    | null
    | { channel: "phone"; destination: string }
    | { channel: "email"; destination: string }
  >(null);

  if (!isAuthenticated) return <Redirect to="/" />;

  return (
    <div className="min-h-screen pb-28">
      <div className="mx-auto max-w-md px-6 pt-8">
        <header className="flex items-center justify-between">
          <Wordmark size="sm" />
        </header>

        <DeletionScheduledBanner />

        <section className="mt-6">
          {meQ.isLoading ? (
            <div className="space-y-2">
              <div className="h-7 w-2/3 animate-pulse rounded bg-navy/10" />
              <div className="h-4 w-1/2 animate-pulse rounded bg-navy/5" />
            </div>
          ) : meQ.data ? (
            <>
              <h1 className="text-2xl font-bold text-navy">{meQ.data.name}</h1>
              <p className="mt-1 flex flex-wrap items-center gap-2 text-sm text-ink-dark">
                <span>{meQ.data.phone}</span>
                {meQ.data.phoneVerified ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2 py-0.5 text-[11px] font-medium text-green-700">
                    ✓ Verified
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() =>
                      setVerifyOpen({
                        channel: "phone",
                        destination: meQ.data.phone,
                      })
                    }
                    className="inline-flex items-center gap-1 rounded-full bg-amber-light px-2.5 py-0.5 text-[11px] font-semibold text-navy hover:bg-amber-light/80"
                    data-testid="button-verify-phone"
                  >
                    Verify
                  </button>
                )}
              </p>
              {meQ.data.email && (
                <p className="mt-1 flex flex-wrap items-center gap-2 text-sm text-ink-dark">
                  <span>{meQ.data.email}</span>
                  {meQ.data.emailVerified ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2 py-0.5 text-[11px] font-medium text-green-700">
                      ✓ Verified
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={() =>
                        setVerifyOpen({
                          channel: "email",
                          destination: meQ.data.email!,
                        })
                      }
                      className="inline-flex items-center gap-1 rounded-full bg-amber-light px-2.5 py-0.5 text-[11px] font-semibold text-navy hover:bg-amber-light/80"
                      data-testid="button-verify-email"
                    >
                      Verify
                    </button>
                  )}
                </p>
              )}
            </>
          ) : (
            <p className="text-red-600">Couldn't load your profile.</p>
          )}
        </section>

        {/* Identity — basic info + the OTP-gated change flows. */}
        <SectionHeader>Identity</SectionHeader>
        <NavList>
          <NavRow href="/profile/edit" label="Edit profile" />
          <NavRow href="/profile/phone-change" label="Change phone number" />
          <NavRow href="/profile/email-change" label="Change email" />
          {/* Phase 26 — customer plate registration. Lives under
              Identity because plates are personal-info metadata. */}
          <NavRow href="/profile/vehicles" label="Vehicles" />
          {/* Phase 31 (D103) — set-password row. Label flips based on
              whether the customer already has a credential. Rendered
              unconditionally (after meQ resolves) so passwordless
              QR-join customers who skipped the post-signup nudge can
              still find their way back. */}
          {meQ.data && (
            <NavRow
              href="/set-password?next=/profile"
              label={
                meQ.data.passwordSet
                  ? "Change password"
                  : "Set up your password"
              }
            />
          )}
        </NavList>

        {/* Relationships — merchants the customer has joined. */}
        <SectionHeader>Relationships</SectionHeader>
        <NavList>
          <NavRow
            href="/profile/connected-merchants"
            label="Connected merchants"
          />
          <NavRow href="/profile/vouchers" label="My vouchers" />
        </NavList>

        {/* Phase 15 — in-app inbox. Surfaces here in addition to the
            BottomNav so customers landing on the profile hub from a
            push-style notification still find the unread badge.
            Phase 18 — push-notification preferences live alongside
            the inbox so the two reachability surfaces stay grouped. */}
        <SectionHeader>Messages</SectionHeader>
        <NavList>
          <NavRow href="/inbox" label="Inbox" badge={unread} />
          <NavRow
            href="/profile/notifications"
            label="Push notifications"
          />
        </NavList>

        {/* Audit & data — for the legally-curious customer. */}
        <SectionHeader>Privacy & data</SectionHeader>
        <NavList>
          <NavRow href="/profile/consents" label="Consent history" />
          <NavRow href="/profile/data-export" label="Download my data" />
        </NavList>

        {/* Help / how-to. Lives above the danger zone so it's
            discoverable on the way out. */}
        <SectionHeader>Help</SectionHeader>
        <NavList>
          <NavRow href="/help" label="How to use Orbit" />
        </NavList>

        {/* Danger zone. */}
        <SectionHeader>Account</SectionHeader>
        <NavList>
          <NavRow
            href="/profile/account-delete"
            label="Delete my account"
            danger
          />
        </NavList>

        <button onClick={signOut} className="btn-secondary w-full mt-6">
          Sign out
        </button>

        <p className="mt-6 text-center text-xs text-ink-dark/70">
          Orbit by M42
        </p>
      </div>
      <BottomNav />

      {verifyOpen && (
        <VerifyChannelDialog
          channel={verifyOpen.channel}
          destination={verifyOpen.destination}
          onClose={() => setVerifyOpen(null)}
        />
      )}
    </div>
  );
}

function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="mt-6 mb-2 text-[11px] font-semibold uppercase tracking-wider text-ink-dark/60">
      {children}
    </h2>
  );
}

function NavList({ children }: { children: React.ReactNode }) {
  return (
    <nav
      className="card divide-y divide-navy/5 p-0 overflow-hidden"
      aria-label="Account section"
    >
      {children}
    </nav>
  );
}

function NavRow(props: {
  href: string;
  label: string;
  danger?: boolean;
  badge?: number;
}) {
  return (
    <Link
      href={props.href}
      className={`w-full flex items-center justify-between px-4 py-3.5 text-left text-sm ${
        props.danger ? "text-red-700" : "text-navy/80"
      }`}
    >
      <span className="flex items-center gap-2">
        <span>{props.label}</span>
        {props.badge != null && props.badge > 0 && (
          <span
            className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-amber px-1.5 text-[10px] font-bold leading-none text-navy"
            data-testid={`badge-${props.label.toLowerCase()}-unread`}
          >
            {props.badge > 99 ? "99+" : props.badge}
          </span>
        )}
      </span>
      <span aria-hidden className="text-navy/40">
        ›
      </span>
    </Link>
  );
}
